import React, { createContext, useState, useContext, useCallback } from "react";

const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  // اول removeNotification رو تعریف کن
  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // بعد showNotification که از removeNotification استفاده می‌کنه
  const showNotification = useCallback(
    ({
      message,
      type = "info",
      duration = 3000,
      actions = [],
      position = "top-center",
      group = null,
    }) => {
      const id = Date.now() + Math.random();

      setNotifications((prev) => {
        let newNotifications = [...prev];

        if (group) {
          newNotifications = newNotifications.filter(
            (n) => !(n.group === group && n.position === position)
          );
        }

        newNotifications.push({
          id,
          message,
          type,
          duration,
          actions,
          position,
          group,
          createdAt: Date.now(),
        });

        return newNotifications;
      });

      return id;
    },
    []
  ); // removeNotification اینجا نیست

  // بقیه توابع که از showNotification استفاده می‌کنن
  const showSuccess = useCallback(
    (message, options = {}) => {
      return showNotification({
        message,
        type: "success",
        group: options.group || "success",
        ...options,
      });
    },
    [showNotification]
  );

  const showError = useCallback(
    (message, options = {}) => {
      return showNotification({
        message,
        type: "error",
        group: options.group || "error",
        ...options,
      });
    },
    [showNotification]
  );

  const showWarning = useCallback(
    (message, options = {}) => {
      return showNotification({
        message,
        type: "warning",
        group: options.group || "warning",
        ...options,
      });
    },
    [showNotification]
  );

  const showInfo = useCallback(
    (message, options = {}) => {
      return showNotification({
        message,
        type: "info",
        group: options.group || "info",
        ...options,
      });
    },
    [showNotification]
  );

  const showFontSize = useCallback(
    (subNumber, size, options = {}) => {
      return showNotification({
        message: `اندازه زیرنویس ${subNumber === 1 ? "یک" : "دو"}: ${size}px`,
        type: "info",
        group: `font-size-${subNumber}`,
        duration: 1000,
        ...options,
      });
    },
    [showNotification]
  );

  const showSubtitleStatus = useCallback(
    (subNumber, status, options = {}) => {
      const statusText = status === "hidden" ? "پنهان" : "نمایش";
      return showNotification({
        message: `زیرنویس ${subNumber === 1 ? "یک" : "دو"} ${statusText} شد`,
        type: "info",
        group: `subtitle-status-${subNumber}`,
        duration: 1500,
        ...options,
      });
    },
    [showNotification]
  );

  const showDelay = useCallback(
    (subNumber, delay, options = {}) => {
      return showNotification({
        message: `تاخیر زیرنویس ${subNumber === 1 ? "یک" : "دو"}: ${delay}ms`,
        type: "info",
        group: `delay-${subNumber}`,
        duration: 1000,
        ...options,
      });
    },
    [showNotification]
  );

  const showConfirm = useCallback(
    (message, onConfirm, onCancel, options = {}) => {
      const id = Date.now() + Math.random();

      const actions = [
        {
          label: "تایید",
          className: "confirm",
          onClick: () => {
            removeNotification(id);
            onConfirm?.();
          },
        },
        {
          label: "لغو",
          className: "cancel",
          onClick: () => {
            removeNotification(id);
            onCancel?.();
          },
        },
      ];

      setNotifications((prev) => {
        let newNotifications = [...prev];

        // حذف confirmهای قبلی
        newNotifications = newNotifications.filter(
          (n) => n.group !== "confirm"
        );

        newNotifications.push({
          id,
          message,
          type: "warning",
          duration: 0,
          actions,
          position: options.position || "top-center",
          group: "confirm",
          createdAt: Date.now(),
        });

        return newNotifications;
      });

      return id;
    },
    [removeNotification]
  );

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const value = {
    notifications,
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showFontSize,
    showSubtitleStatus,
    showDelay,
    showConfirm,
    removeNotification,
    clearAll,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
